<script setup lang="ts">
import { AliyahType, ParashahType } from 'types/types'
import { computed, onMounted, reactive, watch, watchEffect } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { formattedAliyahForTeacher, formattedAliyahForTeacherWithSection } from '@/composables/format'
import { setupAutoSingleSelect } from '@/composables/useAutoSingleSelect'
import { queryAliyot } from '@/store/queryHelpers'
import { compareYear } from '@/util/Aliyah'

interface Props {
  clearable?: boolean
  disabled?: boolean
  only?: Array<AliyahType> | null
  parashah: ParashahType
  preventAutoSingleSelect?: boolean
  remove?: Array<AliyahType>
  resetSelectionOnMultiple?: boolean
}
const xprops = withDefaults(defineProps<Props>(), {
  clearable: true,
  disabled: false,
  only: null,
  preventAutoSingleSelect: false,
  remove: (): Array<AliyahType> => [],
  resetSelectionOnMultiple: false,
})

const state = reactive({
  allAliyot: [] as Array<AliyahType>,
  selectedAliyah: null as AliyahType | null,
})

const logger = useLogger()
const emit = defineEmits(['update:aliyah'])

function fetchAliyot() {
  const parashaId = xprops.parashah?.reading.id ?? -1
  logger.debug('AliyahSelect, fetchAliyot, parashah.reading.id = ')
  logger.debug(parashaId)
  queryAliyot(parashaId).then((aliyot) => {
    logger.debug('AliyahSelect, fetchAliyot result =')
    logger.debug(aliyot)
    state.allAliyot = aliyot
  })
}

onMounted(() => {
  fetchAliyot()

  watch(
    () => xprops.parashah,
    () => {
      fetchAliyot()
    },
  )

  watch(
    () => state.selectedAliyah,
    (newVal) => {
      logger.debug('AliyahSelect; state.selectedAliyah is now newVal =')
      logger.debug(newVal)
    },
    { immediate: true },
  )

  setupAutoSingleSelect({
    filteredItems: filteredAliyot,
    preventAutoSelect: () => xprops.preventAutoSingleSelect,
    resetOnMultiple: () => xprops.resetSelectionOnMultiple,
    setSelected: (item) => {
      state.selectedAliyah = item
    },
  })

  watchEffect(() => {
    emit('update:aliyah', state.selectedAliyah)
  })
})

const filteredAliyot = computed<Array<AliyahType>>(() => {
  logger.debug('AliyahSelect, filteredAliyot called')
  const aliyot = xprops.only ?? state.allAliyot

  return aliyot
    .filter((a: AliyahType) => !xprops.remove.find((b: AliyahType) => a.rank === b.rank))
    .toSorted((a, b) => a.rank - b.rank || compareYear(a, b) || a.variant.localeCompare(b.variant))
})
</script>

<template>
  <v-select
    v-model="state.selectedAliyah"
    :clearable="xprops.clearable"
    :disabled="xprops.disabled"
    :items="filteredAliyot"
    label="Aliyah"
    return-object
    variant="underlined"
  >
    <template #selection="{ item }">
      <v-list-item :title="formattedAliyahForTeacher(item.raw)" />
    </template>

    <template #item="{ item, props }">
      <v-list-item v-bind="props" :title="formattedAliyahForTeacherWithSection(item.raw)" />
    </template>
  </v-select>
</template>
